import { Component, inject, Input, NgZone, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule, FormControl } from '@angular/forms';

import { Card } from '../domain/card';
import { CardActionBase } from '../domain/card-actions/card-action.base';
import { IAddressV2, QuestionBase } from '@jump-tech-frontend/domain';
import { FormUpdate, QuestionHintComponent } from '@jump-tech-frontend/question-components';
import { VideoCaptureComponent } from '@jump-tech-frontend/question-components';
import { AddressLookupV2Component } from '@jump-tech-frontend/address-lookup-v2';
import { GooglePlacesAutocompleteComponent } from './question-components/google-places-autocomplete.component';
import { HtmlQuestionComponent } from './question-components/html-question.component';
import { DateQuestionComponent } from './question-components/date-question/date-question.component';
import { GoogleMapsMultiMarkerQuestionComponent } from './question-components/google-maps-multi-marker-question.component';
import { FileUploadQuestionComponent } from './question-components/file-upload-question/file-upload-question.component';
import { DocumentViewerQuestionComponent } from './question-components/document-viewer-question.component';
import { CheckboxButtonGroupQuestionComponent } from './question-components/checkbox-button-group-question.component';
import { ButtonGroupQuestionComponent } from './question-components/button-group-question.component';
import { ToggleQuestionComponent } from './question-components/toggle-question.component';
import { TextareaQuestionComponent } from './question-components/textarea-question.component';
import { TextboxQuestionComponent } from './question-components/textbox-question.component';
import { MpanInputQuestionComponent } from './question-components/mpan-input-question.component';
import { SignatureQuestionComponent } from './question-components/signature-question.component';
import { MultiImageUploadQuestionComponent } from './question-components/multi-image-upload-question/multi-image-upload-question.component';
import { ImageUploadQuestionComponent } from './question-components/image-upload-question.component';
import { GeneralQuestionComponent } from './question-components/general-question.component';
import { MultiDropdownWithValueQuestionComponent } from './question-components/multi-dropdown-with-value-question.component';
import { DropdownQuestionComponent } from './question-components/dropdown-question.component';
import { GoogleMapsMarkerQuestionComponent } from './question-components/google-maps-marker-question.component';

import {
  getImageCaptureQuestionParams,
  getImageOrFileQuestionParams,
  getOcrCaptureQuestionParams,
  getVideoQuestionParams,
  holdWhileAnalysing,
  ImageAnswer,
  ImageCaptureComponent,
  ImageCaptureQuestionParams,
  ImageOrFileComponent,
  ImageOrFileQuestionParams,
  OcrCaptureComponent,
  OcrCaptureQuestionParams,
  QuestionWrapperComponent
} from '@jump-tech-frontend/question-components';
import { ImageAssetFactory } from '../domain/card';
import { exampleImagesFor } from './example-images';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { MultiImageAiValidationUploadQuestionComponent } from './question-components/multi-image-ai-validation-upload-question/multi-image-ai-validation-upload-question.component';
import { NumberQuestionComponent } from './question-components/number-question/number-question.component';

@Component({
  selector: 'crds-questions',
  template: `
    @for (question of questions; track question) {
      <div>
        <div [formGroup]="form" class="form-group">
          @switch (question.controlType) {
            @case ('AddressDropdownQuestion') {
              <jump-tech-frontend-address-lookup-v2
                [countryCodes]="question.countryCodes"
                [label]="question.label"
                [provider]="question.provider"
                [address]="form.get(question.key).value"
                (addressUpdate)="updateAddress($event, question.key)"
                [required]="question.required"
                [errorMessage]="question.errorMessage"
                [context]="deviceContext"
              >
              </jump-tech-frontend-address-lookup-v2>
            }
            @case ('MapsMarkerQuestion') {
              <crds-question-google-maps-marker [question]="question" [form]="form" [i18ns]="i18ns">
              </crds-question-google-maps-marker>
            }
            @case ('DropdownQuestion') {
              <crds-question-dropdown [question]="question" [form]="form" [i18ns]="i18ns"> </crds-question-dropdown>
            }
            @case ('MultiDropdownQuestion') {
              <crds-question-multi-dropdown-value [question]="question" [form]="form" [i18ns]="i18ns">
              </crds-question-multi-dropdown-value>
            }
            @case ('GeneralText') {
              <crds-question-general [question]="question" [form]="form" [i18ns]="i18ns"> </crds-question-general>
            }
            @case ('ImageUploadQuestion') {
              <crds-question-image-upload [question]="question" [form]="form" [i18ns]="i18ns">
              </crds-question-image-upload>
            }
            @case ('MultiImageUploadQuestion') {
              <crds-question-multi-image-upload
                [question]="question"
                [form]="form"
                [attr.data-qa]="'multiImageUpload'"
                [i18ns]="i18ns"
              >
              </crds-question-multi-image-upload>
            }
            @case ('MultiImageAiValidationQuestion') {
              <crds-question-multi-image-ai-validation-upload
                [question]="question"
                [form]="form"
                [attr.data-qa]="'multiAiValidationImageUpload'"
                [i18ns]="i18ns"
                [deviceContext]="deviceContext"
              >
              </crds-question-multi-image-ai-validation-upload>
            }
            @case ('OcrImageUploadQuestion') {
              <question-wrapper [question]="question" [form]="form" [i18ns]="i18ns">
                <question-ocr-capture
                  (formChange)="handleFormChange($event)"
                  [params]="ocrCaptureParams(question)"
                ></question-ocr-capture>
              </question-wrapper>
            }
            @case ('SignatureQuestion') {
              <crds-question-signature [question]="question" [form]="form" [i18ns]="i18ns"> </crds-question-signature>
            }
            @case ('TextboxQuestion') {
              <crds-question-textbox
                [question]="question"
                [form]="form"
                [i18ns]="i18ns"
                [useJuiControls]="useJuiControls"
                [deviceContext]="deviceContext"
              ></crds-question-textbox>
            }
            @case ('NumberQuestion') {
              <crds-question-number
                [question]="question"
                [form]="form"
                [i18ns]="i18ns"
                [deviceContext]="deviceContext"
              ></crds-question-number>
            }
            @case ('TextareaQuestion') {
              <crds-question-textarea
                [question]="question"
                [form]="form"
                [i18ns]="i18ns"
                [useJuiControls]="useJuiControls"
                [deviceContext]="deviceContext"
              >
              </crds-question-textarea>
            }
            @case ('ToggleQuestion') {
              <crds-question-toggle
                [question]="question"
                [form]="form"
                [i18ns]="i18ns"
                [useJuiControls]="useJuiControls"
                [deviceContext]="deviceContext"
              >
              </crds-question-toggle>
            }
            @case ('ButtonGroupQuestion') {
              <crds-question-button-group
                [question]="question"
                [form]="form"
                [i18ns]="i18ns"
                [useJuiControls]="useJuiControls"
                [deviceContext]="deviceContext"
              >
              </crds-question-button-group>
            }
            @case ('CheckboxButtonGroupQuestion') {
              <crds-question-checkbox-button-group
                [question]="question"
                [form]="form"
                [i18ns]="i18ns"
                [useJuiControls]="useJuiControls"
                [deviceContext]="deviceContext"
              >
              </crds-question-checkbox-button-group>
            }
            @case ('DocumentViewerQuestion') {
              <crds-question-doc-viewer [question]="question" [form]="form" [i18ns]="i18ns"></crds-question-doc-viewer>
            }
            @case ('FileUploadQuestion') {
              <crds-question-file-upload [question]="question" [form]="form" [i18ns]="i18ns">
              </crds-question-file-upload>
            }
            @case ('ImageOrFileUploadQuestion') {
              <question-wrapper [question]="question" [form]="form" [i18ns]="i18ns">
                <question-image-or-file
                  (formChange)="handleFormChange($event)"
                  [params]="imageOrFileParams(question)"
                ></question-image-or-file>
              </question-wrapper>
            }
            @case ('MultiMarkerQuestion') {
              <crds-question-google-maps-multi-marker [question]="question" [form]="form">
              </crds-question-google-maps-multi-marker>
            }
            @case ('DateQuestionComponent') {
              <crds-question-date [question]="question" [form]="form" [i18ns]="i18ns"></crds-question-date>
            }
            @case ('QuestionHint') {
              <question-hint [question]="question"></question-hint>
            }
            @case ('HtmlQuestionComponent') {
              <crds-question-html [question]="question"></crds-question-html>
            }
            @case ('GooglePlacesAddress') {
              <crds-google-places-autocomplete
                [question]="question"
                [form]="form"
                [i18ns]="i18ns"
              ></crds-google-places-autocomplete>
            }
            @case ('AddressTypeaheadQuestion') {
              <jump-tech-frontend-address-lookup-v2
                [countryCodes]="question.countryCodes"
                [label]="question.label"
                [provider]="question.provider"
                [address]="form.get(question.key).value"
                (addressUpdate)="updateAddress($event, question.key)"
                [required]="question.required"
                [errorMessage]="question.errorMessage"
                [context]="deviceContext"
              >
              </jump-tech-frontend-address-lookup-v2>
            }
            @case ('VideoUploadQuestion') {
              <question-wrapper [question]="question" [form]="form" [i18ns]="i18ns">
                <question-video-capture
                  (formChange)="handleFormChange($event)"
                  [params]="getVideoQuestionParams(question, form.get(question.key)?.value)"
                ></question-video-capture>
              </question-wrapper>
            }
            @case ('ImageCaptureQuestion') {
              <question-wrapper [question]="question" [form]="form" [i18ns]="i18ns">
                <question-image-capture
                  (formChange)="handleFormChange($event)"
                  (analysingChange)="holdWhileAnalysing(form.get(question.key), $event)"
                  [params]="imageCaptureParams(question)"
                ></question-image-capture>
              </question-wrapper>
            }
            @case ('MpanQuestion') {
              <crds-question-mpan-input
                [question]="question"
                [form]="form"
                [i18ns]="i18ns"
                [useJuiControls]="useJuiControls"
                [deviceContext]="deviceContext"
              ></crds-question-mpan-input>
            }
          }
        </div>
      </div>
    }
  `,
  styleUrls: ['../sass/relay.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    GoogleMapsMarkerQuestionComponent,
    DropdownQuestionComponent,
    MultiDropdownWithValueQuestionComponent,
    GeneralQuestionComponent,
    ImageUploadQuestionComponent,
    MultiImageUploadQuestionComponent,
    OcrCaptureComponent,
    SignatureQuestionComponent,
    TextboxQuestionComponent,
    TextareaQuestionComponent,
    ToggleQuestionComponent,
    ButtonGroupQuestionComponent,
    CheckboxButtonGroupQuestionComponent,
    DocumentViewerQuestionComponent,
    FileUploadQuestionComponent,
    ImageOrFileComponent,
    GoogleMapsMultiMarkerQuestionComponent,
    DateQuestionComponent,
    QuestionHintComponent,
    HtmlQuestionComponent,
    GooglePlacesAutocompleteComponent,
    AddressLookupV2Component,
    VideoCaptureComponent,
    ImageCaptureComponent,
    QuestionWrapperComponent,
    MultiImageAiValidationUploadQuestionComponent,
    NumberQuestionComponent,
    MpanInputQuestionComponent
  ]
})
export class QuestionsComponent implements OnChanges {
  @Input() card: Card<CardActionBase>;
  @Input() form: UntypedFormGroup;
  @Input() i18ns: I18nKeys;
  @Input() useJuiControls?: boolean = false;
  @Input() deviceContext?: string = 'desktop';

  questions: any;

  getVideoQuestionParams = getVideoQuestionParams;
  holdWhileAnalysing = holdWhileAnalysing;

  imageCaptureParams(question: QuestionBase<ImageAnswer[]>): ImageCaptureQuestionParams {
    return getImageCaptureQuestionParams(question, this.form.get(question.key)?.value, {
      deviceContext: this.deviceContext === 'mobile' ? 'mobile' : 'desktop',
      examples: exampleImagesFor(this.imageAssetFactory, question, this.form)
    });
  }

  ocrCaptureParams(question: QuestionBase<unknown>): OcrCaptureQuestionParams {
    return getOcrCaptureQuestionParams(
      question,
      this.form.get(question.key)?.value,
      exampleImagesFor(this.imageAssetFactory, question, this.form)
    );
  }

  imageOrFileParams(question: QuestionBase<unknown>): ImageOrFileQuestionParams {
    return getImageOrFileQuestionParams(question, this.form.get(question.key)?.value, {
      examples: exampleImagesFor(this.imageAssetFactory, question, this.form)
    });
  }

  private readonly ngZone = inject(NgZone);
  private readonly imageAssetFactory = inject(ImageAssetFactory);

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.card || !Object.prototype.hasOwnProperty.call(changes, 'card')) {
      return;
    }

    this.questions = Object.assign([], this.card.questions);
  }

  updateAddress($event: { address: IAddressV2; location: string }, key: string) {
    const address = $event?.address;
    const isValid = !!address?.postCode?.trim() && !!address?.line1?.trim();
    this.form.get(key)?.setValue(isValid ? address : null);
    this.form.setControl('postCode', new FormControl(address?.postCode));
    this.form.get('installationLatLng')?.setValue($event?.location);
  }

  handleFormChange($event: FormUpdate) {
    this.ngZone.run(() => {
      this.form.controls[$event.key]?.patchValue($event.value);
    });
  }
}
